import React from "react";
import { useChatContext } from "@src/features/assistantChat/ChatRuntimeProvider";
import { useToast } from "@src/lib/hooks/useToast";
import { useAIAssistBrandSelector } from "@src/features/aiAssist/hooks/useAIAssistBrandSelector";
import useTrackClientFacingWorkflow from "@src/features/app/hooks/useTrackClientFacingWorkflow";
import ChatCanvasDialogContainer from "./ChatCanvasDialogContainer";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import {
  OutlineFlowType,
  useOutlineFlow,
} from "@src/features/contentLibrary/outlineFlows.config";
import { useMutation } from "@apollo/client";
import { CreateAiOutlineResultFragment } from "./CreateAiOutlineResult.generated";

type Props = {
  dialogIsOpen: boolean;
  setDialogIsOpen: (open: boolean) => void;
  onItemCreated: (item: CreateAiOutlineResultFragment) => void;
  flowType: OutlineFlowType;
};

const OutlineCanvasDialog: React.FC<Props> = ({
  dialogIsOpen,
  setDialogIsOpen,
  onItemCreated,
  flowType,
}) => {
  const { aiSessionId } = useChatContext();
  const { addErrorToast } = useToast();
  const config = useOutlineFlow(flowType);
  const { selectedSession } = useChatSidebar(config.chatType);

  const { trackClientFacingWorkflow, isTracking } =
    useTrackClientFacingWorkflow({
      onCompleted: (cfw) => {
        if (cfw.result?.libraryItem?.skill?.libraryItem) {
          onItemCreated(cfw.result.libraryItem.skill);
          setDialogIsOpen(false);
        }
      },
      onError: () => {
        addErrorToast({
          callsite: config.callsites.genWorkflowFailed,
        });
      },
    });

  const [startGenWorkflow, { loading: generateMutationLoading }] = useMutation(
    config.mutation.document,
    {
      onCompleted: (data) => {
        const key = Object.keys(data)[0];
        const result = data[key];

        if (result.success && result.clientFacingWorkflow) {
          trackClientFacingWorkflow(result.clientFacingWorkflow);
        } else {
          addErrorToast({
            callsite: config.callsites.startGenWorkflow,
          });
        }
      },
      onError: () => {
        addErrorToast({
          callsite: config.callsites.startGenWorkflow,
        });
      },
    },
  );

  const brandSelectorProps = useAIAssistBrandSelector();

  const handleGenerate = () => {
    startGenWorkflow({
      variables: {
        input: {
          aiChatId: aiSessionId,
          [config.mutation.outlineTextFieldName]:
            selectedSession?.artifact || "",
          brandDetailsId: brandSelectorProps.selectedBrandItem?.brandDetailsId,
        },
      },
    });
  };

  return (
    <ChatCanvasDialogContainer
      open={dialogIsOpen}
      chatType={config.chatType}
      setDialogIsOpen={setDialogIsOpen}
      actionOnClick={handleGenerate}
      actionLabel={config.texts.generateActionLabel}
      actionLoading={generateMutationLoading || isTracking}
      canvasSubtitle={config.texts.canvasSubtitle}
      threadEmptyStateTitle={config.texts.threadEmptyStateTitle}
      threadEmptyStateSubtitle={config.texts.threadEmptyStateSubtitle}
      composerPlaceholder={config.texts.composerPlaceholder}
      brandSelectorProps={brandSelectorProps}
      showConversionProgress={generateMutationLoading || isTracking}
    />
  );
};

export default OutlineCanvasDialog;
